import Image from 'next/image';
import Link from 'next/link';
import type { ArticleItem } from '@/lib/contentful/types';
import { formatDate } from '@/utils/formatDate';
import { extractPlainText } from '@/utils/extractPlainText';
import { estimateReadingTime } from '@/utils/estimateReadingTime';
import { COPY } from '@/lib/copy';

export default function ArticleCard({ article }: { article: ArticleItem }) {
  const title = extractPlainText(article.title);
  const readingTime = estimateReadingTime(article.summary);

  return (
    <Link
      href={`/blog/${article.slug}`}
      aria-label={`${COPY.articleCard.readMorePrefix} ${title}`}
      className="group bg-white flex flex-col transition-transform duration-200 hover:-translate-y-1 no-underline focus-visible:outline-2 focus-visible:outline focus-visible:outline-primary focus-visible:outline-offset-2"
    >
      <div className="aspect-[40/21] overflow-hidden bg-bg-alt shrink-0">
        <Image
          src={`/api/og/blog/${article.slug}`}
          alt=""
          width={800}
          height={420}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
      </div>
      <div className="p-[1.6rem] flex-1 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          {article.tag && (
            <span
              className="font-sans text-[0.6rem] tracking-[0.2em] uppercase font-bold"
              style={{ color: article.tag.textColor ?? 'var(--color-primary)' }}
            >
              {article.tag.label}
            </span>
          )}
          <span className="font-sans text-[0.6rem] text-[var(--text-light)] ml-auto">
            {COPY.articleCard.readingTime(readingTime)}
          </span>
        </div>
        <h3 className="font-serif text-[1.05rem] font-semibold text-text leading-[1.35] flex-1">
          {title}
        </h3>
        {article.summary && (
          <p className="font-sans text-[0.8rem] text-[var(--text-mid)] leading-[1.75] flex-1">
            {article.summary}
          </p>
        )}
        <div className="flex items-center justify-between pt-3 mt-2 border-t border-bg-alt">
          {article.date && (
            <time className="font-sans text-[0.65rem] text-[var(--text-light)]">
              {formatDate(article.date)}
            </time>
          )}
          <span className="font-sans text-[0.68rem] font-bold tracking-[0.1em] uppercase text-primary transition-colors group-hover:text-[var(--color-primary-dark)]">
            Ler mais <span aria-hidden="true">→</span>
          </span>
        </div>
      </div>
    </Link>
  );
}
